export function waitForActivation(registration:ServiceWorkerRegistration):Promise<ServiceWorker> {
  return new Promise((resolve,reject)=>{
    const watched=new Set<ServiceWorker>();
    const cleanup=()=>{registration.removeEventListener('updatefound',inspect);for(const worker of watched)worker.removeEventListener('statechange',inspect);};
    const inspect=()=>{
      const active=registration.active;
      if(active?.state==='activated'){cleanup();resolve(active);return;}
      const workers=[registration.installing,registration.waiting,active].filter((w):w is ServiceWorker=>!!w);
      for(const worker of workers)if(!watched.has(worker)){watched.add(worker);worker.addEventListener('statechange',inspect);}
      // navigator.serviceWorker.ready never rejects. An unsuccessful first
      // installation must release application startup rather than wait forever.
      if(!workers.length||workers.every(w=>w.state==='redundant')){cleanup();reject(new Error('Installazione offline non riuscita'));}
    };
    registration.addEventListener('updatefound',inspect);inspect();
  });
}

export type UpdateProgress='idle'|'checking'|'downloading'|'ready'|'current'|'error'|'offline'|'applying';
export interface OfflineControls {
  checkForUpdates:()=>Promise<void>;
  activateUpdate:()=>Promise<void>;
  dispose:()=>void;
}

export async function prepareOffline(
  onStatus:(text:string)=>void,
  onUpdate:(registration:ServiceWorkerRegistration)=>void,
  onControllerChange:()=>void,
  onUpdateProgress:(progress:UpdateProgress)=>void
):Promise<OfflineControls|undefined> {
  if(!('serviceWorker'in navigator)) {onStatus('Offline non supportato');return;}
  if(import.meta.env.DEV) {onStatus('Anteprima sviluppo');return;}

  let registration:ServiceWorkerRegistration|undefined;
  let disposed=false,checking:Promise<void>|null=null,manualChecking=false,lastCheck=0,stopMonitoring=()=>{},checkOfflineListener:(()=>void)|null=null,activationTimer:number|undefined,activationResolve:(()=>void)|null=null,activationReject:((reason?:unknown)=>void)|null=null;
  const watched=new Map<ServiceWorker,()=>void>();
  let lastController=navigator.serviceWorker.controller;
  const updateUI=(progress:UpdateProgress)=>{if(!disposed)onUpdateProgress(progress);};
  const reportUpdate=(reg:ServiceWorkerRegistration)=>{if(disposed)return;onUpdate(reg);updateUI('ready');};
  const inspectUpdate=(worker:ServiceWorker)=>{
    if(watched.has(worker))return;
    const inspect=()=>{
      if(disposed||!registration)return;
      if(worker.state==='installed'&&navigator.serviceWorker.controller&&registration.waiting)reportUpdate(registration);
      else if(worker.state==='installing')updateUI('downloading');
      else if(worker.state==='redundant'&&!registration.waiting)updateUI('error');
    };
    watched.set(worker,inspect);
    worker.addEventListener('statechange',inspect);
  };
  const handleUpdateFound=()=>{
    if(!registration)return;
    const worker=registration.installing;
    if(worker){updateUI('downloading');inspectUpdate(worker);}
  };
  const handleControllerChange=()=>{
    const controller=navigator.serviceWorker.controller;
    const changed=!!lastController&&!!controller&&lastController!==controller;
    lastController=controller;
    if(changed&&!disposed){
      if(activationTimer!==undefined){clearTimeout(activationTimer);activationTimer=undefined;}
      activationResolve?.();activationResolve=null;activationReject=null;
      onControllerChange();
    }
  };

  // Listen before registering: a repaired or newly installed worker can claim
  // this page while register() is still resolving.
  navigator.serviceWorker.addEventListener('controllerchange',handleControllerChange);
  try {
    registration=await navigator.serviceWorker.register('/sw.js',{updateViaCache:'none'});
    registration.addEventListener('updatefound',handleUpdateFound);
    if(registration.installing)inspectUpdate(registration.installing);
    if(registration.waiting)reportUpdate(registration);

    const checkForUpdates=(manual=false):Promise<void>=>{
      if(checking){if(manual){manualChecking=true;updateUI('checking');}return checking;}
      if(!registration)return Promise.resolve();
      if(!navigator.onLine){if(manual)updateUI('offline');return Promise.resolve();}
      if(!manual&&Date.now()-lastCheck<30_000)return Promise.resolve();
      lastCheck=Date.now();
      manualChecking=manual;
      const activeRegistration=registration;
      const run=async()=>{
        try{
          if(manualChecking)updateUI('checking');
          await activeRegistration.update();
          if(disposed)return;
          if(activeRegistration.waiting)reportUpdate(activeRegistration);
          else if(activeRegistration.installing){updateUI('downloading');inspectUpdate(activeRegistration.installing);}
          else if(manualChecking)updateUI('current');
        }catch{
          if(manualChecking)updateUI('error');
        }
      };
      checking=run().finally(()=>{checking=null;manualChecking=false;});
      return checking;
    };
    const activateUpdate=():Promise<void>=>new Promise((resolve,reject)=>{
      const waiting=registration?.waiting;
      if(!waiting||!lastController){reject(new Error('Aggiornamento non più disponibile'));return;}
      updateUI('applying');
      activationResolve=resolve;activationReject=reject;
      activationTimer=window.setTimeout(()=>{
        activationTimer=undefined;activationResolve=null;activationReject=null;updateUI('error');reject(new Error('Attivazione non riuscita'));
      },15_000);
      try{waiting.postMessage({type:'ACTIVATE_UPDATE'});}catch(error){
        clearTimeout(activationTimer);activationTimer=undefined;activationResolve=null;activationReject=null;updateUI('error');reject(error);
      }
    });
    const lifecycleCheck=()=>{void checkForUpdates();};
    lifecycleCheck();
    const visibleOnline=()=>document.visibilityState==='visible'&&navigator.onLine;
    const timer=window.setInterval(()=>{if(visibleOnline())lifecycleCheck();},5*60_000);
    const handleVisibility=()=>{if(visibleOnline())lifecycleCheck();};
    const handleOnline=()=>lifecycleCheck();
    window.addEventListener('pageshow',handleVisibility);
    document.addEventListener('visibilitychange',handleVisibility);
    window.addEventListener('focus',handleVisibility);
    window.addEventListener('online',handleOnline);
    stopMonitoring=()=>{
      window.clearInterval(timer);
      window.removeEventListener('pageshow',handleVisibility);
      if(checkOfflineListener)window.removeEventListener('pageshow',checkOfflineListener);
      document.removeEventListener('visibilitychange',handleVisibility);
      window.removeEventListener('focus',handleVisibility);
      window.removeEventListener('online',handleOnline);
      navigator.serviceWorker.removeEventListener('controllerchange',handleControllerChange);
      registration?.removeEventListener('updatefound',handleUpdateFound);
      for(const [worker,listener] of watched)worker.removeEventListener('statechange',listener);
      watched.clear();
    };

    await waitForActivation(registration);
    const verify=()=>new Promise<boolean>((resolve)=>{
      const channel=new MessageChannel();
      const finish=(ready:boolean)=>{clearTimeout(timeout);channel.port1.close();resolve(ready);};
      const timeout=setTimeout(()=>finish(false),6000);
      channel.port1.onmessage=e=>finish(e.data.ready===true);
      if(registration?.active)registration.active.postMessage({type:'VERIFY_OFFLINE'},[channel.port2]);else finish(false);
    });
    const checkOffline=async()=>onStatus(await verify()?'Disponibile offline':'Salvataggio offline incompleto');
    checkOfflineListener=checkOffline;
    await checkOffline();
    window.addEventListener('pageshow',checkOffline);
    // Initial installation has no waiting update. Leave the manual check
    // available once the active worker and its offline cache are ready.
    if(!registration.waiting&&!registration.installing)updateUI('idle');

    return {
      checkForUpdates:()=>checkForUpdates(true),
      activateUpdate,
      dispose:()=>{
        disposed=true;
        if(activationTimer!==undefined)clearTimeout(activationTimer);
        activationTimer=undefined;activationResolve=null;activationReject?.(new Error('Pagina chiusa'));activationReject=null;
        stopMonitoring();
      }
    };
  }catch{
    disposed=true;
    stopMonitoring();
    navigator.serviceWorker.removeEventListener('controllerchange',handleControllerChange);
    onStatus('Offline non disponibile');
  }
}
